import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { LogOut } from 'lucide-react';
import { markNotificationsAsRead, clearAllNotifications } from '../../store/slices/notificationsSlice';
import {
    sortNotificationsByTimestampDesc,
    NOTIFICATION_MAX_OFFLINE_IN_UI,
} from '../../constants/notificationConfig';
import './Notifications.css';

const NotificationIcon = ({ type }) => {
    if (type === 'entry') return <span className="notification-icon entry">→</span>;
    if (type === 'exit') return <span className="notification-icon exit">←</span>;
    if (type === 'offline') return <span className="notification-icon offline">📴</span>;
    if (type === 'sos') return <span className="notification-icon sos">🆘</span>;
    return null;
};

const formatGeofenceName = (name) => {
    if (!name) return '';
    return name.replace(/^beach_/, '').replace(/_/g, ' ');
};

const formatSmartDuration = (mins) => {
    if (!mins || mins <= 0) return 'a moment';
    const days = Math.floor(mins / 1440);
    const hours = Math.floor((mins % 1440) / 60);
    const remaining = mins % 60;
    if (days > 0) {
        const parts = [`${days} day${days !== 1 ? 's' : ''}`];
        if (hours > 0) parts.push(`${hours} hr${hours !== 1 ? 's' : ''}`);
        return parts.join(' ');
    }
    if (hours > 0) {
        const parts = [`${hours} hr${hours !== 1 ? 's' : ''}`];
        if (remaining > 0) parts.push(`${remaining} min${remaining !== 1 ? 's' : ''}`);
        return parts.join(' ');
    }
    return `${mins} min${mins !== 1 ? 's' : ''}`;
};

const formatNotificationMessage = (notif) => {
    if (notif.eventType === 'sos') {
        return (
            <>
                <strong>LG Band {notif.lgBandId || notif.deviceName || notif.deviceId}</strong>
                {' raised '}
                <strong>SOS</strong>
                {notif.geofenceName && !notif.outsideGeofence && (
                    <>{' in '}<strong>{formatGeofenceName(notif.geofenceName)}</strong></>
                )}
                {notif.outsideGeofence && (
                    <>
                        <br />
                        <span className="notification-sos-outside">
                            SOS: Incident alert, outside Geofenced Zone
                            {notif.nearestBeach ? ` · Nearest Beach: ${notif.nearestBeach}` : ''}
                            {' · Call the police: 112 / 100'}
                        </span>
                    </>
                )}
            </>
        );
    }
    if (notif.eventType === 'offline') {
        return (
            <>
                <strong>{notif.deviceName}</strong>
                {' has been '}
                <strong>offline</strong>
                {' for '}
                <strong>{formatSmartDuration(notif.offlineMins)}</strong>
            </>
        );
    }
    return (
        <>
            <strong>{notif.deviceName}</strong>
            {notif.eventType === 'entry' ? ' entered ' : ' exited '}
            <strong>{formatGeofenceName(notif.geofenceName)}</strong>
        </>
    );
};

const NotificationItem = ({ notif, onNotificationClick, onClose }) => {
    const isOffline = notif.eventType === 'offline';
    const isSos = notif.eventType === 'sos';
    const typeClass = isSos ? 'is-sos' : (isOffline ? 'is-offline' : 'is-geofence');
    const clickable = !!(onNotificationClick && notif.deviceId);
    return (
        <li
            key={notif.id}
            className={`notification-item ${typeClass} ${clickable ? 'clickable' : 'not-clickable'}`}
            onClick={() => {
                if (clickable) {
                    onNotificationClick(notif.deviceId, notif);
                    onClose();
                }
            }}
            style={{ cursor: clickable ? 'pointer' : 'default' }}
        >
            <NotificationIcon type={notif.eventType} />
            <div className="notification-details">
                <p>{formatNotificationMessage(notif)}</p>
                <p className="timestamp">
                    {notif.timestamp ?
                        (notif.timestamp.seconds ?
                            new Date(notif.timestamp.seconds * 1000).toLocaleString() :
                            new Date(notif.timestamp).toLocaleString()
                        ) :
                        'Unknown time'
                    }
                </p>
                {clickable && (
                    <p className="click-hint">{isSos ? 'Click to locate on map' : 'Click to view device'}</p>
                )}
            </div>
        </li>
    );
};

const SectionHeader = ({ label, count, isExpanded, onToggle, headerClass = 'geofence-header' }) => (
    <li
        className={`notifications-section-header ${headerClass} collapsible`}
        onClick={onToggle}
        role="button"
        aria-expanded={isExpanded}
    >
        <span className="section-header-label">
            {label} ({count})
        </span>
        <span className={`section-chevron ${isExpanded ? 'expanded' : ''}`} aria-hidden="true">
            ▾
        </span>
    </li>
);

const NotificationPanel = ({ notifications, onNotificationClick, onClose, userId, dispatch }) => {
    const offlineNotifs = sortNotificationsByTimestampDesc(
        notifications.filter(n => n.eventType === 'offline')
    ).slice(0, NOTIFICATION_MAX_OFFLINE_IN_UI);
    const sosNotifs = sortNotificationsByTimestampDesc(notifications.filter(n => n.eventType === 'sos'));
    const geofenceNotifs = notifications.filter(n => n.eventType !== 'offline' && n.eventType !== 'sos');

    const [sosExpanded, setSosExpanded] = useState(true);
    const [offlineExpanded, setOfflineExpanded] = useState(true);
    const [geofenceExpanded, setGeofenceExpanded] = useState(true);

    const toggleSos = useCallback(() => setSosExpanded(v => !v), []);
    const toggleOffline = useCallback(() => setOfflineExpanded(v => !v), []);
    const toggleGeofence = useCallback(() => setGeofenceExpanded(v => !v), []);

    return createPortal(
        <>
            <div className="notifications-backdrop" onClick={onClose} />
            <div className="notifications-panel">
                <div className="notifications-header">
                    <span>Notifications</span>
                    {notifications.length > 0 && (
                        <button
                            className="clear-all-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm('Clear all notifications? This cannot be undone.')) {
                                    dispatch(clearAllNotifications(userId));
                                }
                            }}
                        >
                            Clear All
                        </button>
                    )}
                </div>
                <ul className="notifications-list">
                    <SectionHeader
                        label="SOS Alerts"
                        count={sosNotifs.length}
                        isExpanded={sosExpanded}
                        onToggle={toggleSos}
                        headerClass="sos-header"
                    />
                    {sosExpanded && (
                        sosNotifs.length > 0 ? (
                            sosNotifs.map(notif => (
                                <NotificationItem
                                    key={notif.id}
                                    notif={notif}
                                    onNotificationClick={onNotificationClick}
                                    onClose={onClose}
                                />
                            ))
                        ) : (
                            <li className="notifications-section-empty">No SOS alerts</li>
                        )
                    )}
                    <SectionHeader
                        label="Geofence Events"
                        count={geofenceNotifs.length}
                        isExpanded={geofenceExpanded}
                        onToggle={toggleGeofence}
                        headerClass="geofence-header"
                    />
                    {geofenceExpanded && (
                        geofenceNotifs.length > 0 ? (
                            geofenceNotifs.map(notif => (
                                <NotificationItem
                                    key={notif.id}
                                    notif={notif}
                                    onNotificationClick={onNotificationClick}
                                    onClose={onClose}
                                />
                            ))
                        ) : (
                            <li className="notifications-section-empty">No geofence events</li>
                        )
                    )}
                    <SectionHeader
                        label="Offline Devices"
                        count={offlineNotifs.length}
                        isExpanded={offlineExpanded}
                        onToggle={toggleOffline}
                        headerClass="geofence-header"
                    />
                    {offlineExpanded && (
                        offlineNotifs.length > 0 ? (
                            offlineNotifs.map(notif => (
                                <NotificationItem
                                    key={notif.id}
                                    notif={notif}
                                    onNotificationClick={onNotificationClick}
                                    onClose={onClose}
                                />
                            ))
                        ) : (
                            <li className="notifications-section-empty">No offline devices</li>
                        )
                    )}
                </ul>
            </div>
        </>,
        document.body
    );
};

const Notifications = ({ onNotificationClick, variant = 'dark', onLogout }) => {
    const dispatch = useDispatch();
    const { notifications } = useSelector(state => state.notifications);
    const user = useSelector(state => state.auth.user);
    const [isOpen, setIsOpen] = useState(false);
    const unreadCount = notifications.filter(n => !n.isRead).length;
    const bellRef = useRef(null);

    const userId = (() => {
        const email = typeof user === 'string' ? user : user?.email;
        if (!email) return null;
        return email.replace(/[.@]/g, m => m === '.' ? '_' : '_at_');
    })();

    const togglePanel = () => {
        setIsOpen(!isOpen);
        if (!isOpen && unreadCount > 0) {
            dispatch(markNotificationsAsRead(userId));
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    return (
        <div className={`notifications-container ${variant === 'light' ? 'light' : ''}`} ref={bellRef}>
            <div className="header-action-buttons">
                <button className="bell-icon-button" onClick={togglePanel} aria-label="Toggle notifications">
                    <span className="bell-icon" aria-hidden="true">🔔</span>
                    {unreadCount > 0 && (
                        <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                    )}
                </button>
                {onLogout && (
                    <button
                        type="button"
                        className="logout-icon-button lg:hidden"
                        aria-label="Logout"
                        title="Logout"
                        onClick={(event) => {
                            event.stopPropagation();
                            onLogout();
                        }}
                    >
                        <LogOut size={20} strokeWidth={2.25} aria-hidden="true" />
                    </button>
                )}
            </div>

            {isOpen && (
                <NotificationPanel
                    notifications={notifications}
                    onNotificationClick={onNotificationClick}
                    onClose={() => setIsOpen(false)}
                    userId={userId}
                    dispatch={dispatch}
                />
            )}
        </div>
    );
};

export default Notifications;
